import { useMemo } from 'react';

interface CapabilityPickerProps {
  capabilities: string[];
  onChange: (next: string[]) => void;
}

// Hardcoded product slugs the picker knows about. Operators can declare
// capabilities for products outside this list — the picker preserves them
// silently. Replacing this with a discovery API is filed as
// 52-followup-plugins-product-discovery.
const KNOWN_PRODUCTS: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'vendors', label: 'Vendors' },
  { slug: 'contacts', label: 'Contacts' },
  { slug: 'assets', label: 'Assets' },
  { slug: 'permits', label: 'Permits' },
  { slug: 'books', label: 'Books' },
  { slug: 'decisions', label: 'Decisions' },
  { slug: 'cashflow', label: 'CashFlow' },
  { slug: 'pipeline', label: 'Pipeline' },
  { slug: 'inventory', label: 'Inventory' },
  { slug: 'warranties', label: 'Warranties' },
  { slug: 'skills', label: 'Skills' },
  { slug: 'workforce', label: 'Workforce' },
  { slug: 'playbooks', label: 'Playbooks' },
  { slug: 'graph', label: 'Graph' },
  { slug: 'clients', label: 'Clients' },
];

const TOKEN_PREFIX = 'skarn.read.';

function tokenFor(slug: string): string {
  return `${TOKEN_PREFIX}${slug}`;
}

export function CapabilityPicker({ capabilities, onChange }: CapabilityPickerProps) {
  const knownTokens = useMemo(() => new Set(KNOWN_PRODUCTS.map((p) => tokenFor(p.slug))), []);
  const unknownTokens = useMemo(
    () => capabilities.filter((c) => !knownTokens.has(c)),
    [capabilities, knownTokens],
  );
  const checkedSet = useMemo(() => new Set(capabilities), [capabilities]);

  function toggle(slug: string, checked: boolean) {
    const token = tokenFor(slug);
    const known = KNOWN_PRODUCTS.map((p) => tokenFor(p.slug));
    const newKnownSelection = checked
      ? Array.from(new Set([...capabilities.filter((c) => known.includes(c)), token]))
      : capabilities.filter((c) => known.includes(c) && c !== token);
    // Always keep unknown tokens (forward-compat for products outside KNOWN_PRODUCTS)
    onChange([...unknownTokens, ...newKnownSelection]);
  }

  return (
    <div className="rb-section">
      <h3 className="rb-section-title">Host capabilities</h3>
      <p className="rb-hint">
        Select the products this plugin needs read access to. Each token grants
        access to the corresponding product's internal API.
      </p>
      <div className="rb-row" style={{ flexWrap: 'wrap', gap: '8px 16px' }}>
        {KNOWN_PRODUCTS.map((p) => {
          const id = `cap-${p.slug}`;
          const token = tokenFor(p.slug);
          return (
            <label key={p.slug} htmlFor={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <input
                id={id}
                type="checkbox"
                checked={checkedSet.has(token)}
                onChange={(e) => toggle(p.slug, e.target.checked)}
              />
              <span>{p.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
